import { memo, useEffect, useRef, useState } from 'react';
import { useFilterBridge } from '../../context/FilterBridgeContext';
import './DashboardHeaderSettings.css';

function SettingsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.26.6.77 1.03 1.41 1.11H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const LiveStatusPill = memo(function LiveStatusPill({
  isLive,
  wsStatus,
  liveLabel,
  lastWsLabel,
}) {
  return (
    <span
      className={`header-settings__live${isLive ? ' header-settings__live--on' : ''} header-settings__live--${wsStatus}`}
      role="status"
    >
      <span className="header-settings__live-dot" aria-hidden="true" />
      {liveLabel}
      {lastWsLabel && wsStatus === 'connected' && (
        <span className="header-settings__live-time"> · {lastWsLabel}</span>
      )}
    </span>
  );
});

export default function DashboardHeaderSettings({
  isAdmin = false,
  onOpenCreateUser,
  onOpenTotp,
}) {
  const { filters } = useFilterBridge();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const showCreate = Boolean(isAdmin && onOpenCreateUser);
  const showTotp = Boolean(onOpenTotp);
  const hasAccountActions = showCreate || showTotp;

  const {
    wsStatus,
    wsLabel,
    lastWsLabel,
    showReconnect,
    reconnectLabel,
    onReconnect,
    error,
    menuToolbar,
  } = filters || {};

  const isLive = wsStatus === 'connected' || wsStatus === 'rest_only';
  const liveLabel =
    wsStatus === 'connected'
      ? 'Live Websocket'
      : wsStatus === 'rest_only'
        ? 'Live (REST)'
        : wsLabel;

  const hasDashContent = Boolean(
    error || menuToolbar || showReconnect || wsStatus || liveLabel
  );

  if (!hasDashContent && !hasAccountActions) return null;

  function closeAnd(fn) {
    setOpen(false);
    fn?.();
  }

  return (
    <div className="header-settings" ref={rootRef}>
      <button
        type="button"
        className={`menu__icon-btn header-settings__btn${open ? ' menu__icon-btn--active' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? 'Close settings' : 'Open settings'}
        aria-expanded={open}
        aria-haspopup="true"
        title="Settings"
      >
        <SettingsIcon />
      </button>

      {open && (
        <div className="header-settings__panel" role="dialog" aria-label="Settings">
          {hasAccountActions ? (
            <div className="header-settings__account">
              {showCreate ? (
                <button
                  type="button"
                  className="header-settings__action"
                  onClick={() => closeAnd(onOpenCreateUser)}
                >
                  Create user
                </button>
              ) : null}
              {showTotp ? (
                <button
                  type="button"
                  className="header-settings__action"
                  onClick={() => closeAnd(onOpenTotp)}
                >
                  Authenticator
                </button>
              ) : null}
            </div>
          ) : null}

          {hasDashContent ? (
            <>
              {error && (
                <span className="header-settings__error" role="alert">
                  {error}
                </span>
              )}

              <div className="header-settings__status-row">
                <LiveStatusPill
                  isLive={isLive}
                  wsStatus={wsStatus || 'disconnected'}
                  liveLabel={liveLabel || 'Offline'}
                  lastWsLabel={lastWsLabel}
                />

                {showReconnect && onReconnect && (
                  <button
                    type="button"
                    className="header-settings__reconnect"
                    onClick={onReconnect}
                  >
                    <RefreshIcon />
                    {reconnectLabel === 'Reconnect WS'
                      ? 'Reconnect'
                      : reconnectLabel || 'Reconnect'}
                  </button>
                )}
              </div>

              {menuToolbar ? (
                <div className="header-settings__tools">{menuToolbar}</div>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
